import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// --- Token persistence (localStorage fallback when cookies fail on custom domains) ---
const ACCESS_KEY = "tsu_access_token";
const REFRESH_KEY = "tsu_refresh_token";

export const tokenStore = {
  getAccess: () => {
    try { return localStorage.getItem(ACCESS_KEY); } catch { return null; }
  },
  getRefresh: () => {
    try { return localStorage.getItem(REFRESH_KEY); } catch { return null; }
  },
  set: (access, refresh) => {
    try {
      if (access) localStorage.setItem(ACCESS_KEY, access);
      if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
    } catch {}
  },
  clear: () => {
    try {
      localStorage.removeItem(ACCESS_KEY);
      localStorage.removeItem(REFRESH_KEY);
    } catch {}
  },
};

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Attach Bearer header on every request (in addition to cookie)
api.interceptors.request.use((config) => {
  const t = tokenStore.getAccess();
  if (t) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${t}`;
    }
  }
  return config;
});

// --- Auto-refresh interceptor: on 401, try /auth/refresh once and retry ---
let isRefreshing = false;
let pending = [];

const subscribe = (cb) => pending.push(cb);
const flush = (success) => {
  pending.forEach((cb) => cb(success));
  pending = [];
};

async function doRefresh() {
  const refreshToken = tokenStore.getRefresh();
  const { data } = await axios.post(
    `${API}/auth/refresh`,
    { refresh_token: refreshToken || undefined },
    {
      withCredentials: true,
      headers: refreshToken ? { "x-refresh-token": refreshToken } : {},
    }
  );
  if (data?.access_token) {
    tokenStore.set(data.access_token, data.refresh_token);
  }
  return data;
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const url = original?.url || "";
    const isAuthCall =
      url.includes("/auth/refresh") ||
      url.includes("/auth/login") ||
      url.includes("/auth/register") ||
      url.includes("/auth/logout");
    if (status !== 401 || original?._retry || isAuthCall) {
      return Promise.reject(error);
    }
    original._retry = true;
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        subscribe((ok) => {
          if (ok) resolve(api(original));
          else reject(error);
        });
      });
    }
    isRefreshing = true;
    try {
      await doRefresh();
      isRefreshing = false;
      flush(true);
      // ensure retry picks up new token via request interceptor
      if (original.headers) delete original.headers.Authorization;
      return api(original);
    } catch (e) {
      isRefreshing = false;
      flush(false);
      tokenStore.clear();
      return Promise.reject(error);
    }
  }
);

export default api;

export function fileUrl(relativeOrAbs) {
  if (!relativeOrAbs) return "/default-avatar.png";
  if (relativeOrAbs.startsWith("http")) return relativeOrAbs;
  // Static asset served by frontend (default avatar, logo etc.)
  if (relativeOrAbs.startsWith("/default-") || relativeOrAbs.startsWith("/icons/") || relativeOrAbs.startsWith("/apple-")) {
    return relativeOrAbs;
  }
  // Backend-stored file (e.g. /uploads/abc.png) — attach access token so <img> can authenticate
  const t = tokenStore.getAccess();
  const sep = relativeOrAbs.includes("?") ? "&" : "?";
  return `${BACKEND_URL}${relativeOrAbs}${t ? `${sep}auth=${encodeURIComponent(t)}` : ""}`;
}

export async function uploadImage(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/uploads/image", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Etwas ist schiefgelaufen. Bitte erneut versuchen.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const LOGO_URL =
  "https://customer-assets-v7afamib.emergentagent.net/job_carrier-chat/artifacts/ae5321c8bc302ccd_TSU%20Partners%20Logo%20mit%20Stra%C3%9Fen-Swoosh%20%281%29.png";

export const WEBSITE_URL = "https://tsupartners.de";
